import Roadmap16 from '@carbon/icons-react/es/roadmap/16';
import { Skeleton as AntSkeleton, Space as AntSpace } from 'antd';
import React, { FC, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { FieldOptionBase } from 'api/tasks';
import { Filter } from 'components/molecules/filters/types';
import {
  HoverableTag,
  TriggerOptionProps,
} from 'components/organisms/field-editors/components/controlled-select-based-editor-trigger-content';
import { TagEditorComponents } from 'components/organisms/tasks/criteria/editor-components';
import { SingleSelectEditor } from 'components/organisms/tasks/criteria/single-select-editor';
import { SingleSelectField } from 'components/organisms/tasks/fields';
import { SelectFilterOperationType } from 'components/organisms/tasks/filters/select';
import { useTranslate } from 'hooks/use-translation';
import { TemplateItemInfo } from './use-filter';

const Space = styled(AntSpace)`
  .ant-space-item .single-select-editor {
    padding-left: 0 !important;

    span {
      font-size: var(--font-size-base);
      font-weight: bold;
      margin-left: 0;
    }
  }
`;

const Skeleton = styled(AntSkeleton)`
  .ant-skeleton-content {
    vertical-align: middle;

    .ant-skeleton-paragraph {
      margin: 0;
    }
  }
`;
const TriggerTag = styled(HoverableTag)`
  max-width: 30ch;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: var(--font-weight-normal) !important;
`;
const TriggerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

export const TagTriggerOption: FC<TriggerOptionProps> = ({
  option: { color, label },
}) => <TriggerTag color={color}>{label}</TriggerTag>;

type Props = {
  options: { ids: number[]; items: Array<TemplateItemInfo> };
  selection: {
    id?: number;
    data?: FieldOptionBase<number>;
  };
  savedId?: number;
  updateTemplateInFilter: (value: number, shouldReplace?: boolean) => Filter;
  isLoading: boolean;
  templateField: SingleSelectField;
};

export const TemplateFilter: FC<Props> = ({
  options,
  selection,
  savedId,
  updateTemplateInFilter,
  isLoading,
  templateField,
}) => {
  const translate = useTranslate();
  const onChange = useCallback(
    (value) => {
      if (value) {
        updateTemplateInFilter(value);
      }
    },
    [updateTemplateInFilter],
  );

  const { id: selectedId } = selection;

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (selectedId != null) {
      return;
    }

    const id = savedId || options.ids[0];

    if (selectedId === id) {
      return;
    }

    updateTemplateInFilter(id, true);
  }, [isLoading, updateTemplateInFilter, selectedId, savedId, options.ids]);

  if (isLoading) {
    return <Skeleton title={false} paragraph={{ rows: 1, width: ['25ch'] }} />;
  }

  return (
    <Space size={4}>
      <TriggerTitle>
        <Roadmap16 />
        {translate('reports.template_insight.template_picker.title')}:
      </TriggerTitle>
      <SingleSelectEditor
        components={{
          ...TagEditorComponents,
          ClearIndicator: () => null,
          TriggerOption: TagTriggerOption,
        }}
        field={templateField}
        filterType={SelectFilterOperationType.Is}
        options={options.items as unknown as FieldOptionBase<number>[]}
        value={selection.data?.value ?? null}
        onChange={onChange}
        type="tag"
      />
    </Space>
  );
};
